import { Grid, Table, Typography } from "antd"
import type { TableColumnsType } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { RowCardList } from "../rows/RowCardList"
import { neutral } from "../tokens"
import { compareCellValues, formatCellValue, rowDrillTarget, rowsPayloadOf } from "../rowsFormat"
import type { RowRecord, WidgetProps, WidgetState } from "../types"
import { WidgetFrame } from "../WidgetFrame"

export const TableWidget = ({
  widget,
  payload,
  state,
  currency,
  onRefresh,
  onExpand,
  onDrill
}: WidgetProps) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const screens = Grid.useBreakpoint()
  const data = payload ? rowsPayloadOf(payload) : undefined
  const title = widget.config?.title ?? widget.title
  const effectiveCurrency = currency ?? payload?.currency

  let effectiveState: WidgetState
  if (state && state !== "ready") {
    effectiveState = state
  } else if (!data) {
    effectiveState = "loading"
  } else if (data.rows.length === 0) {
    effectiveState = "empty"
  } else {
    effectiveState = "ready"
  }

  const columns: TableColumnsType<RowRecord> = (data?.columns ?? []).map((col) => ({
    key: col.key,
    dataIndex: col.key,
    title: col.title,
    sorter: (a, b) => compareCellValues(a[col.key], b[col.key], col.unit),
    render: (value: unknown) =>
      formatCellValue(value, col.unit, {
        currency: effectiveCurrency,
        language: i18n.language
      })
  }))

  const rows = (data?.rows ?? []).map((row, index) => ({
    key: String((row as { id?: unknown }).id ?? index),
    ...row
  }))

  const goTo = (target: string) => (onDrill ? onDrill(target) : navigate(target))

  // UI_UX_GUIDELINES §5.1: below md a table renders as cards, not as a scroller.
  const asCards = !screens.md

  const limit = widget.config?.limit
  const total = data?.total
  const shown = rows.length
  const showTotalCaption = typeof total === "number" && total > shown

  return (
    <WidgetFrame
      title={title}
      state={effectiveState}
      capabilities={widget.capabilities}
      widgetId={widget.id}
      payload={payload}
      onRefresh={onRefresh}
      onExpand={onExpand}
    >
      {asCards && data ? (
        <RowCardList
          data={data}
          currency={effectiveCurrency}
          pageSize={limit}
          onOpen={goTo}
        />
      ) : (
        <Table
          size="small"
          columns={columns}
          dataSource={rows}
          // Without an explicit x, rc-table builds no horizontal scroller and the
          // table paints past the card border rather than shrinking to it.
          scroll={{ x: "max-content" }}
          pagination={limit ? { pageSize: limit, hideOnSinglePage: true } : false}
          onRow={(row) => {
            const target = rowDrillTarget(row)
            if (!target) return {}
            return {
              style: { cursor: "pointer" },
              onClick: () => goTo(target)
            }
          }}
        />
      )}
      {showTotalCaption && (
        <Typography.Text
          data-testid="table-total-caption"
          style={{ display: "block", marginTop: 8, fontSize: 12, color: neutral.muted }}
        >
          {t("widget.table.showingOfTotal", "Showing {{shown}} of {{total}}", {
            shown,
            total
          })}
        </Typography.Text>
      )}
    </WidgetFrame>
  )
}
